/**
 * Avatar face picker component with image grid.
 *
 * The grid is the face catalogue scoped to the video-avatar provider in force,
 * so a stored face saved under a different vendor has no tile. It gets one
 * anyway — marked unavailable — because a silently de-selected grid reads as "no
 * face was ever chosen" and hides that calls are now using the default face
 * (WI 93a13e0b).
 */
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle, Loader2, UserCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '../../../lib/utils';
import { Card } from '../../../components/ui/card';
import { avatarFacesApi } from '../../../modules/avatars/services/avatarFacesApi';
import { EntityLoadState } from '../common';
import { SELECTABLE_TILE_FOCUS, selectableTileProps } from './selectableTileProps';
import type { AvatarFace } from '../../../modules/avatars/types';

interface AvatarFacePickerProps {
  value?: string;
  onChange: (faceId: string | null) => void;
  disabled?: boolean;
}

export const AvatarFacePicker = ({
  value,
  onChange,
  disabled = false,
}: AvatarFacePickerProps) => {
  const { t } = useTranslation('common');
  const { data: faces = [], isLoading, error } = useQuery({
    queryKey: ['avatarFaces'],
    queryFn: avatarFacesApi.listFaces,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center p-6">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <EntityLoadState error={error} className="mb-4" />
    );
  }

  const storedFaceIsGone = !!value && !faces.some((face: AvatarFace) => face.id === value);

  return (
    <div className="space-y-2">
      {storedFaceIsGone && (
        <p className="flex items-center gap-1.5 text-xs text-warning">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
          {t('avatars.faceUnavailableHint')}
        </p>
      )}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-4">
        {/* Not selectable — there is nothing to select — so no role, no tab stop,
            and no aria-label: a div's generic role does not take a name from the
            author, and the label is rendered as visible text below. */}
        {storedFaceIsGone && (
          <Card
            data-testid="avatar-face-unavailable"
            className="relative cursor-default overflow-hidden border-warning ring-2 ring-warning"
          >
            <div className="aspect-square flex items-center justify-center bg-muted">
              <AlertTriangle className="h-12 w-12 text-warning" />
            </div>
            <div className="p-2">
              <p className="truncate text-center text-sm">{t('avatars.faceUnavailable')}</p>
            </div>
          </Card>
        )}
        <Card
          {...selectableTileProps({
            selected: !value,
            disabled,
            onSelect: () => onChange(null),
          })}
          className={cn(
            'relative overflow-hidden transition-all cursor-pointer',
            SELECTABLE_TILE_FOCUS,
            disabled && 'opacity-60 cursor-not-allowed',
            !value
              ? 'ring-2 ring-primary border-primary'
              : 'hover:border-primary/50'
          )}
        >
          <div className="aspect-square flex items-center justify-center bg-muted">
            <UserCircle className="h-12 w-12 text-muted-foreground" />
          </div>
          <div className="p-2">
            <p className="text-sm text-center truncate">{t('avatars.none')}</p>
          </div>
          {!value && (
            <div className="absolute top-1 right-1">
              <CheckCircle className="h-5 w-5 text-primary bg-card rounded-full" />
            </div>
          )}
        </Card>
        {faces.map((face: AvatarFace) => (
          <Card
            key={face.id}
            data-testid="avatar-face"
            {...selectableTileProps({
              selected: value === face.id,
              disabled,
              onSelect: () => onChange(face.id),
            })}
            aria-label={face.name}
            className={cn(
              'relative overflow-hidden transition-all cursor-pointer',
              SELECTABLE_TILE_FOCUS,
              disabled && 'opacity-60 cursor-not-allowed',
              value === face.id
                ? 'ring-2 ring-primary border-primary'
                : 'hover:border-primary/50'
            )}
          >
            {/* A face with no preview falls back to the SAME placeholder the "None"
                tile uses. It used to point at /images/avatars/default.png, a file
                that has never existed in this repo, so the one catalogue face
                without a preview rendered the browser's broken-image icon. */}
            <div className="aspect-square">
              {face.preview_url ? (
                <img
                  src={face.preview_url}
                  alt={face.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-muted">
                  <UserCircle className="h-12 w-12 text-muted-foreground" />
                </div>
              )}
            </div>
            <div className="p-2">
              <p className="text-sm text-center truncate">{face.name}</p>
            </div>
            {value === face.id && (
              <div className="absolute top-1 right-1">
                <CheckCircle className="h-5 w-5 text-primary bg-card rounded-full" />
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};
